import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getChannels,
  getOutputs,
  getProgramGroups,
  getProfiles,
  getTxChains,
  getDataCenters,
  getDevices,
  getDeployments
} from '../utilities/mezzanineApi';

const MezzanineContext = createContext();

export const useMezzanine = () => {
  const context = useContext(MezzanineContext);
  if (!context) {
    throw new Error('useMezzanine must be used within a MezzanineProvider');
  }
  return context;
};

export const MezzanineProvider = ({ children }) => {
  // Channels
  const [channels, setChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [channelsError, setChannelsError] = useState(null);

  // Outputs
  const [outputs, setOutputs] = useState([]);
  const [outputsLoading, setOutputsLoading] = useState(false);
  const [outputsError, setOutputsError] = useState(null);

  // Program Groups
  const [programGroups, setProgramGroups] = useState([]);
  const [programGroupsLoading, setProgramGroupsLoading] = useState(false);
  const [programGroupsError, setProgramGroupsError] = useState(null);

  // Profiles
  const [profiles, setProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [profilesError, setProfilesError] = useState(null);

  // TX Chains
  const [txChains, setTxChains] = useState([]);
  const [txChainsLoading, setTxChainsLoading] = useState(false);
  const [txChainsError, setTxChainsError] = useState(null);

  // Data Centers
  const [dataCenters, setDataCenters] = useState([]);
  const [dataCentersLoading, setDataCentersLoading] = useState(false);
  const [dataCentersError, setDataCentersError] = useState(null);

  // Devices
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState(null);

  // Deployments
  const [deployments, setDeployments] = useState([]);
  const [deploymentsLoading, setDeploymentsLoading] = useState(false);
  const [deploymentsError, setDeploymentsError] = useState(null);

  // Fetch functions
  const fetchChannels = useCallback(async () => {
    setChannelsLoading(true);
    setChannelsError(null);
    try {
      const response = await getChannels();
      setChannels(response.channels || []);
    } catch (err) {
      console.error('Error fetching channels:', err);
      setChannelsError(err.message || 'Failed to fetch channels');
    } finally {
      setChannelsLoading(false);
    }
  }, []);

  const fetchOutputs = useCallback(async () => {
    setOutputsLoading(true);
    setOutputsError(null);
    try {
      const response = await getOutputs();
      setOutputs(response.outputs || []);
    } catch (err) {
      console.error('Error fetching outputs:', err);
      setOutputsError(err.message || 'Failed to fetch outputs');
    } finally {
      setOutputsLoading(false);
    }
  }, []);

  const fetchProgramGroups = useCallback(async () => {
    setProgramGroupsLoading(true);
    setProgramGroupsError(null);
    try {
      const response = await getProgramGroups();
      setProgramGroups(response.program_groups || []);
    } catch (err) {
      console.error('Error fetching program groups:', err);
      setProgramGroupsError(err.message || 'Failed to fetch program groups');
    } finally {
      setProgramGroupsLoading(false);
    }
  }, []);

  const fetchProfiles = useCallback(async () => {
    setProfilesLoading(true);
    setProfilesError(null);
    try {
      const response = await getProfiles();
      setProfiles(response.profiles || []);
    } catch (err) {
      console.error('Error fetching profiles:', err);
      setProfilesError(err.message || 'Failed to fetch profiles');
    } finally {
      setProfilesLoading(false);
    }
  }, []);

  const fetchTxChains = useCallback(async () => {
    setTxChainsLoading(true);
    setTxChainsError(null);
    try {
      const response = await getTxChains();
      setTxChains(response.tx_chains || []);
    } catch (err) {
      console.error('Error fetching tx chains:', err);
      setTxChainsError(err.message || 'Failed to fetch tx chains');
    } finally {
      setTxChainsLoading(false);
    }
  }, []);

  const fetchDataCenters = useCallback(async () => {
    setDataCentersLoading(true);
    setDataCentersError(null);
    try {
      const response = await getDataCenters();
      setDataCenters(response.data_centers || []);
    } catch (err) {
      console.error('Error fetching data centers:', err);
      setDataCentersError(err.message || 'Failed to fetch data centers');
    } finally {
      setDataCentersLoading(false);
    }
  }, []);

  const fetchDevices = useCallback(async () => {
    setDevicesLoading(true);
    setDevicesError(null);
    try {
      const response = await getDevices();
      setDevices(response.devices || []);
    } catch (err) {
      console.error('Error fetching devices:', err);
      setDevicesError(err.message || 'Failed to fetch devices');
    } finally {
      setDevicesLoading(false);
    }
  }, []);

  const fetchDeployments = useCallback(async () => {
    setDeploymentsLoading(true);
    setDeploymentsError(null);
    try {
      const response = await getDeployments();
      setDeployments(response.deployments || []);
    } catch (err) {
      console.error('Error fetching deployments:', err);
      setDeploymentsError(err.message || 'Failed to fetch deployments');
    } finally {
      setDeploymentsLoading(false);
    }
  }, []);

  // Fetch all data on mount
  const fetchAllData = useCallback(() => {
    fetchChannels();
    fetchOutputs();
    fetchProgramGroups();
    fetchProfiles();
    fetchTxChains();
    fetchDataCenters();
    fetchDevices();
    fetchDeployments();
  }, [fetchChannels, fetchOutputs, fetchProgramGroups, fetchProfiles, fetchTxChains, fetchDataCenters, fetchDevices, fetchDeployments]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const value = {
    // Channels
    channels,
    channelsLoading,
    channelsError,
    fetchChannels,
    // Outputs
    outputs,
    outputsLoading,
    outputsError,
    fetchOutputs,
    // Program Groups
    programGroups,
    programGroupsLoading,
    programGroupsError,
    fetchProgramGroups,
    // Profiles
    profiles,
    profilesLoading,
    profilesError,
    fetchProfiles,
    // TX Chains
    txChains,
    txChainsLoading,
    txChainsError,
    fetchTxChains,
    // Data Centers
    dataCenters,
    dataCentersLoading,
    dataCentersError,
    fetchDataCenters,
    // Devices
    devices,
    devicesLoading,
    devicesError,
    fetchDevices,
    // Deployments
    deployments,
    deploymentsLoading,
    deploymentsError,
    fetchDeployments,
    // Fetch all
    fetchAllData,
  };

  return (
    <MezzanineContext.Provider value={value}>
      {children}
    </MezzanineContext.Provider>
  );
};

export default MezzanineContext;
